import React, { useState } from "react";
import classes from "./VendorCard.module.scss";
import CustomImage from "../../../CustomImage";
import TickMark from "../../../UI/Icons/TickMark";
import InfoIcon from "../../../UI/Icons/InfoIcon";
import { Vendor, VendorCountry } from "../Vendors";
import { useRouter } from "next/router";
import { getOnboardingOrFormCtaProps } from "../../../../utils/applyCtaButtonProps";
import { persistSourceContext } from "../../../../utils/b2c/sourceTracking";
import { PRIMARY_SOURCE_B2C_WIDGET } from "../../../../utils/b2c/constants";

const DESTINATION_CHIPS_COLLAPSED_COUNT = 6;

const VendorDestinationChips = ({ destinations }: { destinations: VendorCountry[] }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const hiddenCount = destinations?.length - DESTINATION_CHIPS_COLLAPSED_COUNT;
  const visibleDestinations = isExpanded ? destinations : destinations?.slice(0, DESTINATION_CHIPS_COLLAPSED_COUNT);

  return (
    <div className={classes["destination-chips"]}>
      {visibleDestinations.map((destination) => (
        <span key={destination.id} className={classes["destination-chip"]}>
          <CustomImage src={destination?.imageUrl ?? ""} width={16} height={12} alt={destination?.name} objectFit="cover" />
          {destination?.name}
        </span>
      ))}
      {hiddenCount > 0 && (
        <button type="button" className={classes["more-toggle"]} onClick={() => setIsExpanded((prev) => !prev)}>
          {isExpanded ? "Show less" : `+${hiddenCount} More`}
        </button>
      )}
    </div>
  );
};

interface VendorCardProps {
  vendor: Vendor;
  onLearnMore?: (vendor: Vendor) => void;
  onEnquireNow?: (vendor: Vendor) => void;
}

const VendorCard = ({ vendor, onLearnMore, onEnquireNow }: VendorCardProps) => {
  const router = useRouter();

  const enquireNowButtonProps = getOnboardingOrFormCtaProps(router, {
    btnLink: vendor?.cardDetails?.enquireButton?.link,
    formId: vendor?.cardDetails?.enquireButton?.formId,
    toOnboardingEnabled: vendor?.cardDetails?.enquireButton?.toOnboardingEnabled,
    sourceUrl: typeof window !== "undefined" ? window.location.pathname : "",
  });

  const ctaBtnClickHandler = (buttonProps: any, receivedData: any) => (e: React.MouseEvent<HTMLButtonElement>) => {
    if (buttonProps.mode === "onboarding") {
      persistSourceContext({
        sourceUrl: window.location.href,
        primarySource: PRIMARY_SOURCE_B2C_WIDGET,
        fifthStepVisible: receivedData?.button?.nearestOffice ?? true,
        coachingVisible: receivedData?.button?.lookingForCoaching ?? true,
      });
    }
    buttonProps.onClick?.(e);
  };

  return (
    <div className={classes["vendor-card"]}>
      <div className={classes["part-1"]}>
        <div className={classes["vendor-badge"]}>
          <CustomImage src={vendor?.logo?.url ?? ""} width={244} height={72} alt={vendor?.logo?.name ?? ""} objectFit="cover" />
        </div>

        <h3 className={classes["card-title"]}>{vendor?.cardDetails?.title}</h3>

        <div className={classes["divider"]} />

        <div className={classes["promotions-header"]}>
          <span className={classes["promotions-label"]}>{vendor?.cardDetails?.promotionsAndFeatures?.title}</span>
          {vendor?.cardDetails?.additionalInfo && (
            <span className={classes["info-tooltip-wrapper"]} tabIndex={0}>
              <InfoIcon width={16} height={16} color="#64647A" />
              <span className={classes["info-tooltip-box"]} role="tooltip">
                {vendor?.cardDetails?.additionalInfo}
              </span>
            </span>
          )}
        </div>

        <ul className={classes["promotions-list"]}>
          {vendor?.cardDetails?.promotionsAndFeatures?.features?.length > 0 &&
            vendor?.cardDetails?.promotionsAndFeatures?.features?.map((promotion) => (
              <li key={promotion.id} className={classes["promotions-item"]}>
                <span className={classes["check-circle"]}>
                  <TickMark width={10} height={10} color="#226CF5" />
                </span>
                {promotion?.title && <div dangerouslySetInnerHTML={{ __html: promotion.title }} />}
              </li>
            ))}
        </ul>

        <p className={classes["destinations-label"]}>{vendor?.cardDetails?.destinationHeading ?? "Available for these destinations:"}</p>
        <VendorDestinationChips destinations={vendor.countries} />
      </div>

      <div className={classes["part-2"]}>
        <div className={classes["divider"]} />

        <div className={classes["card-actions"]}>
          {vendor?.cardDetails?.learnMoreButton?.link && (
            <a
              href={vendor?.cardDetails?.learnMoreButton?.link ?? `${window.location.pathname}/${vendor?.slug}`}
              className={classes["learn-more-btn"]}
              onClick={onLearnMore ? () => onLearnMore(vendor) : undefined}
              target="_blank"
            >
              Learn more
            </a>
          )}
          <button
            type="button"
            className={`${classes["enquire-now-btn"]} ${enquireNowButtonProps.formClassName}`}
            onClick={(e) => {
              onEnquireNow?.(vendor);
              ctaBtnClickHandler(enquireNowButtonProps, vendor?.cardDetails?.enquireButton)?.(e);
            }}
          >
            Enquire Now
          </button>
        </div>

        {<p className={classes["disclaimer"]}>{vendor?.cardDetails?.disclaimerText ?? "*subject to conditions"}</p>}
      </div>
    </div>
  );
};

export default VendorCard;
